import { useState } from 'react';
import { ArrowPathIcon, BoltIcon, PlusIcon } from '@heroicons/react/24/outline';
import {
    useSparkCredsStatus,
    useSyncSparkCreds,
    useTestSparkCreds
} from '../../../../hooks/useSparkCreds';
import { useToast } from '../../../../providers/ToastProvider';
import { sparkCredsSyncErrorMessage } from '../../../../services/sparkCreds';
import type { SparkCredsLiveCheck, SparkCredsStatus } from '../../../../services/sparkCreds';
import { apiErrorMessage } from '../../../../utils/apiError';
import { formatDateTime } from '../../../../utils/dates';
import {
    ButtonSpinner,
    DESTRUCTIVE_BUTTON_CLASS,
    NEUTRAL_BUTTON_CLASS
} from './CredsActionButtons';
import { SparkCredsForceRegenerateModal } from './SparkCredsForceRegenerateModal';

interface SparkCredsActionProps {
    practiceId: string;
}

// Which button kicked off the in-flight sync, so its spinner lands on the
// right button (regenerate's pending state renders inside the confirm modal).
type SyncOrigin = 'refresh' | 'generate' | 'regenerate';

const SYNC_ACTION_LABEL: Record<string, string> = {
    generated: 'generated',
    refreshed: 'refreshed',
    regenerated: 'regenerated'
};

function StatusLine({
    status,
    isLoading
}: {
    status: SparkCredsStatus | undefined;
    isLoading: boolean;
}) {
    if (isLoading) {
        return <div className="h-3.5 w-40 bg-gray-200 rounded animate-pulse" />;
    }
    if (!status?.hasCreds) {
        return (
            <span className="inline-flex items-center gap-1.5 text-[12px] text-gray-500">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
                No credentials
            </span>
        );
    }

    const expiresAt = status.accessTokenExpiresAt;
    const isExpired = expiresAt !== null && new Date(expiresAt).getTime() < Date.now();
    return (
        <span
            className={`inline-flex items-center gap-1.5 text-[12px] ${isExpired ? 'text-red-600' : 'text-gray-600'}`}
        >
            <span
                className={`w-1.5 h-1.5 rounded-full shrink-0 ${isExpired ? 'bg-red-500' : 'bg-green-500'}`}
            />
            {expiresAt
                ? `Token stored · ${isExpired ? 'expired' : 'expires'} ${formatDateTime(expiresAt)}`
                : 'Token stored'}
        </span>
    );
}

function TestResultLine({ result }: { result: SparkCredsLiveCheck }) {
    if (result.ok) {
        return <span className="text-[12px] font-medium text-green-700">Token valid</span>;
    }
    return (
        <span className="text-[12px] font-medium text-red-600">
            Token check failed{result.reason ? ` - ${result.reason}` : ''}
        </span>
    );
}

// Sits below the sparkAcct field (as an accessory - see AccountSection) once
// it holds a value - the non-empty counterpart to RequestSparkAccountAction,
// which only renders while the field is empty. Everything here is about the
// stored OAuth credentials themselves, not the sparkAcct field's own value.
export function SparkCredsAction({ practiceId }: SparkCredsActionProps) {
    const { showToast } = useToast();
    const [isForceModalOpen, setIsForceModalOpen] = useState(false);
    const [syncOrigin, setSyncOrigin] = useState<SyncOrigin | null>(null);

    const status = useSparkCredsStatus(practiceId);
    const test = useTestSparkCreds(practiceId);
    const sync = useSyncSparkCreds(practiceId);

    const handleTest = () => {
        test.mutate();
    };

    const handleSync = (origin: SyncOrigin) => {
        setSyncOrigin(origin);
        sync.mutate(origin === 'regenerate', {
            onSuccess: (result) => {
                showToast(
                    `Spark token ${SYNC_ACTION_LABEL[result.action] ?? result.action} for ${result.practiceName}`,
                    'success'
                );
                setIsForceModalOpen(false);
            },
            onError: (error) => {
                showToast(sparkCredsSyncErrorMessage(error), 'error');
            },
            onSettled: () => {
                setSyncOrigin(null);
            }
        });
    };

    const hasCreds = status.data?.hasCreds ?? false;
    const isBusy = sync.isPending || test.isPending;
    const isSyncing = (origin: SyncOrigin) => sync.isPending && syncOrigin === origin;

    return (
        <div className="flex flex-col gap-2 border border-gray-200 rounded-lg bg-gray-50 px-3 py-2.5 w-full">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <StatusLine status={status.data} isLoading={status.isLoading} />
                <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                    <button
                        type="button"
                        onClick={handleTest}
                        disabled={isBusy || !hasCreds}
                        className={NEUTRAL_BUTTON_CLASS}
                    >
                        {test.isPending ? <ButtonSpinner /> : <BoltIcon className="h-3.5 w-3.5" />}
                        Test
                    </button>
                    <button
                        type="button"
                        onClick={() => handleSync('refresh')}
                        disabled={isBusy || !hasCreds}
                        className={NEUTRAL_BUTTON_CLASS}
                    >
                        {isSyncing('refresh') ? (
                            <ButtonSpinner />
                        ) : (
                            <ArrowPathIcon className="h-3.5 w-3.5" />
                        )}
                        Refresh
                    </button>
                    <button
                        type="button"
                        onClick={() => handleSync('generate')}
                        disabled={isBusy || hasCreds || status.isLoading}
                        className={NEUTRAL_BUTTON_CLASS}
                    >
                        {isSyncing('generate') ? (
                            <ButtonSpinner />
                        ) : (
                            <PlusIcon className="h-3.5 w-3.5" />
                        )}
                        Generate
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsForceModalOpen(true)}
                        disabled={isBusy || !hasCreds}
                        className={DESTRUCTIVE_BUTTON_CLASS}
                    >
                        <ArrowPathIcon className="h-3.5 w-3.5" />
                        Regenerate
                    </button>
                </div>
            </div>

            {test.isError && !test.isPending && (
                <span className="text-[12px] font-medium text-red-600">
                    {apiErrorMessage(test.error, 'Failed to test the stored token')}
                </span>
            )}
            {test.data?.live && !test.isPending && <TestResultLine result={test.data.live} />}

            {sync.isPending && syncOrigin !== 'regenerate' && (
                <span className="text-[11px] text-gray-500">This can take a minute…</span>
            )}

            {isForceModalOpen && (
                <SparkCredsForceRegenerateModal
                    isPending={sync.isPending}
                    onConfirm={() => handleSync('regenerate')}
                    onCancel={() => setIsForceModalOpen(false)}
                />
            )}
        </div>
    );
}
